import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import type { ProfileRelease } from '../profileReleases';
import { profileMessages as m } from '../profileMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileReleaseGridProps {
  releases: readonly ProfileRelease[];
  value: string | undefined;
  label: string;
  onPick: (catalogItemUuid: string) => void;
}

/** The releases with a cover, newest first (design b1 `.rel`): the cover, the title, "Out 16 Oct · Neon". */
const ProfileReleaseGridComponent: React.FC<ProfileReleaseGridProps> = ({ releases, value, label, onPick }) => {
  const intl = useIntl();
  const lineOf = (release: ProfileRelease): string => {
    if (release.releaseDate === null) {
      return intl.formatMessage(m.releaseNoDate);
    }
    const date = intl.formatDate(release.releaseDate, { day: 'numeric', month: 'short' });
    return release.look === null
      ? intl.formatMessage(m.releaseOut, { date })
      : intl.formatMessage(m.releaseOutTheme, {
          date,
          theme: intl.formatMessage(releaseThemeMessages[release.look.themeId].name),
        });
  };
  return (
    <div className={styles.releases} role="radiogroup" aria-label={label} data-testid="profile-release-grid">
      {releases.map((release) => {
        const isOn = release.catalogItemUuid === value;
        return (
          <button
            key={release.catalogItemUuid}
            type="button"
            role="radio"
            aria-checked={isOn}
            className={clsx(styles.release, isOn && styles.releaseOn)}
            data-testid={`profile-release-${release.catalogItemUuid}`}
            onClick={() => onPick(release.catalogItemUuid)}
          >
            <span className={styles.releaseArt} style={{ backgroundImage: `url("${release.artworkUrl}")` }} />
            <span className={styles.grow}>
              <b className="d-block text-truncate">{release.title}</b>
              <small>{lineOf(release)}</small>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const ProfileReleaseGrid = TypedMemo(ProfileReleaseGridComponent);
